import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

// Analysis Types
export interface KeyMetric {
  metric: string
  value: string | number | boolean
}

export interface VerificationCategory {
  category: string
  correctness: number // 0-100
}

export interface ValidationItem {
  category: string
  status: 'PASSED' | 'WARNING' | 'ERROR' | 'PENDING' | 'FAILED'
  warnings?: string[]
  errors?: string[]
}

export interface DocumentAnalysis {
  documentId: string
  workId: string
  status: 'pending' | 'processing' | 'complete' | 'error'
  
  // Tab 1: Key Metrics
  keyMetrics: KeyMetric[]
  
  // Tab 2: Verification
  verification: VerificationCategory[]
  
  // Tab 3: Validation
  validation: ValidationItem[]
  
  // Metadata
  createdAt: number
  updatedAt: number
  error?: string
}

interface AnalysisState {
  // Normalized by documentId
  byDocumentId: Record<string, DocumentAnalysis>
  
  // Currently selected document for viewing
  selectedDocumentId: string | null
  
  // Loading state
  isLoading: boolean
  error: string | null
}

const initialState: AnalysisState = {
  byDocumentId: {},
  selectedDocumentId: null,
  isLoading: false,
  error: null
}

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    // Set complete analysis for a document
    setAnalysis: (state, action: PayloadAction<DocumentAnalysis>) => {
      const analysis = action.payload
      state.byDocumentId[analysis.documentId] = analysis
    },
    
    // Update analysis status
    updateAnalysisStatus: (state, action: PayloadAction<{
      documentId: string
      status: DocumentAnalysis['status']
    }>) => {
      const { documentId, status } = action.payload
      if (state.byDocumentId[documentId]) {
        state.byDocumentId[documentId].status = status
        state.byDocumentId[documentId].updatedAt = Date.now()
      }
    },
    
    // Set key metrics
    setKeyMetrics: (state, action: PayloadAction<{
      documentId: string
      metrics: KeyMetric[]
    }>) => {
      const { documentId, metrics } = action.payload
      if (state.byDocumentId[documentId]) {
        state.byDocumentId[documentId].keyMetrics = metrics
        state.byDocumentId[documentId].updatedAt = Date.now()
      }
    },
    
    // Set verification data
    setVerification: (state, action: PayloadAction<{
      documentId: string
      verification: VerificationCategory[]
    }>) => {
      const { documentId, verification } = action.payload
      if (state.byDocumentId[documentId]) {
        state.byDocumentId[documentId].verification = verification
        state.byDocumentId[documentId].updatedAt = Date.now()
      }
    },
    
    // Set validation data
    setValidation: (state, action: PayloadAction<{
      documentId: string
      validation: ValidationItem[]
    }>) => {
      const { documentId, validation } = action.payload
      if (state.byDocumentId[documentId]) {
        state.byDocumentId[documentId].validation = validation
        state.byDocumentId[documentId].updatedAt = Date.now()
      }
    },
    
    // Initialize pending analysis
    initializePendingAnalysis: (state, action: PayloadAction<{
      documentId: string
      workId: string
    }>) => {
      const { documentId, workId } = action.payload
      state.byDocumentId[documentId] = {
        documentId,
        workId,
        status: 'pending',
        keyMetrics: [],
        verification: [],
        validation: [],
        createdAt: Date.now(),
        updatedAt: Date.now()
      }
    },
    
    // Select document for viewing
    selectDocument: (state, action: PayloadAction<string | null>) => {
      state.selectedDocumentId = action.payload
    },
    
    // Clear analysis for a document
    clearAnalysis: (state, action: PayloadAction<string>) => {
      delete state.byDocumentId[action.payload]
      if (state.selectedDocumentId === action.payload) {
        state.selectedDocumentId = null
      }
    },
    
    // Clear all analysis data
    clearAllAnalysis: (state) => {
      state.byDocumentId = {}
      state.selectedDocumentId = null
    },
    
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    }
  }
})

export const {
  setAnalysis,
  updateAnalysisStatus,
  setKeyMetrics,
  setVerification,
  setValidation,
  initializePendingAnalysis,
  selectDocument,
  clearAnalysis,
  clearAllAnalysis,
  setLoading,
  setError
} = analysisSlice.actions

export default analysisSlice.reducer

// Selectors
export const selectAnalysisByDocumentId = (documentId: string) => 
  (state: { analysis: AnalysisState }) => state.analysis.byDocumentId[documentId]

export const selectSelectedAnalysis = (state: { analysis: AnalysisState }) => {
  const { selectedDocumentId, byDocumentId } = state.analysis
  if (!selectedDocumentId) return null
  return byDocumentId[selectedDocumentId] || null
}

export const selectAnalysisStatus = (documentId: string) =>
  (state: { analysis: AnalysisState }) => state.analysis.byDocumentId[documentId]?.status || 'pending'

